/**
 * Role capabilities for Prasaar.
 *
 * Workflow:
 * - maker creates → submit for review
 * - checker verifies / edits / rejects → submit for publish (approved)
 * - admin only → final publish to feed
 */

export function isStaff(role) {
  return role === 'admin' || role === 'maker' || role === 'checker';
}

/** Create drafts and submit for review */
export function canCreate(role) {
  return role === 'admin' || role === 'maker';
}

/** Manage ingest sources */
export function canManageSources(role) {
  return role === 'admin' || role === 'maker';
}

/** Compose broadcast alerts */
export function canComposeAlerts(role) {
  return role === 'admin' || role === 'checker';
}

/** Approve (submit for publish) or reject pending articles */
export function canApprove(role) {
  return role === 'admin' || role === 'checker';
}

/** Whether this user may review a specific pending article in the UI */
export function canReviewArticle(role, article, userId) {
  if (!canApprove(role) || article?.status !== 'pending') return false;
  if (article.createdById && userId && article.createdById === userId) {
    return false;
  }
  const authorRole = article.createdByRole || 'maker';
  if (role === 'admin') return true;
  if (role === 'checker') {
    return authorRole !== 'checker' && authorRole !== 'admin';
  }
  return false;
}

/** Admin can final-publish an approved article */
export function canPublishApproved(role, article) {
  return role === 'admin' && article?.status === 'approved';
}

/** Audience options shown in compose UI */
export function composeAudienceOptions(role) {
  if (role === 'admin') {
    return [
      { value: 'all', label: 'Everyone' },
      { value: 'reader', label: 'Readers' },
      { value: 'maker', label: 'Makers' },
      { value: 'checker', label: 'Checkers' },
      { value: 'admin', label: 'Admins' },
    ];
  }
  if (role === 'checker') {
    return [
      { value: 'reader', label: 'Readers' },
      { value: 'maker', label: 'Makers' },
    ];
  }
  return [];
}

export function defaultComposeAudience(role) {
  if (role === 'admin') return ['all'];
  if (role === 'checker') return ['reader', 'maker'];
  return [];
}

/** Admin-only force publish */
export function canAdminPublish(role) {
  return role === 'admin';
}

/** Admin user management */
export function canManageUsers(role) {
  return role === 'admin';
}

/** Full-app analytics (admin only) */
export function canViewAnalytics(role) {
  return role === 'admin';
}
